'use client';
import * as React from 'react';
import { ColorPicker as AntdColorPicker, Button, Col, Divider, InputNumber, Row, Tooltip } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import type { AggregationColor } from 'antd/es/color-picker/color';
import type { DynamicComponentProps } from '../../../types/commontypes';
import { extractHex, hexToRgba, rgbToRgbaConvert } from '@/utils/rgbTorgba';
import { red, yellow, lime, green, cyan, blue, geekblue, purple, magenta, orange } from '@ant-design/colors';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import styles from '../../../styles/global.module.css';
import { entries } from '../../../utils/utils';
import { OpacitySlider } from './OpacitySlider';

const palettes = {
  red,
  orange,
  yellow,
  lime,
  green,
  cyan,
  blue,
  geekblue,
  purple,
  magenta,
};

const ColorBox = ({
  color,
  onClick,
  selected,
  onContextMenu,
  svgIcon: _svgIcon,
}: {
  color: string;
  onClick: () => void;
  selected?: boolean;
  onContextMenu?: (e: React.MouseEvent<HTMLDivElement>) => void;
  svgIcon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}) => {
  const isWhite =
    color.toLowerCase() === '#fff' ||
    color.toLowerCase() === '#ffffff' ||
    color.toLowerCase() === 'white' ||
    color === 'rgb(255, 255, 255)';
  return (
    <div className={`${styles.colorbox_wrapper}`}>
      <div
        className={`size-4 rounded cursor-pointer border
          ${isWhite ? 'border-[#dbdbdb]-400 border' : 'border-transparent'}   
          hover:border-blue-400`}
        style={{ backgroundColor: color }}
        onClick={onClick}
        onContextMenu={onContextMenu}
      />
      {selected && <div className={`${styles.colorbox_selected_overlay}`} />}
      {/* <div className={`${styles.colorbox_hover_overlay}`} /> */}
    </div>
  );
};

const ColorPicker: React.FC<DynamicComponentProps<string> & { disabledAlpha?: boolean }> = ({
  value,
  onChange,
  disabled,
  isFromMiniToolbar,
  svgIcon: SvgIcon,
  label,
  disabledAlpha: disabledAlphaProp,
}) => {
  const [color, setColor] = useState<string>(value || '#1d55d7');
  const [showPicker, setShowPicker] = useState(false);
  const [hexColor, setHexColor] = useState<string>(value || '#1d55d7');
  const [opacity, setOpacity] = useState<number>(100);
  const [isHovered, setIsHovered] = useState(false);
  // Load initial custom colors from localStorage (if any)
  const [customColors, setCustomColors] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('customColors');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Save to localStorage whenever customColors changes
  useEffect(() => {
    localStorage.setItem('customColors', JSON.stringify(customColors));
  }, [customColors]);

  const [contextMenu, setContextMenu] = useState<{
    visible: boolean;
    x: number;
    y: number;
    colorToRemove: string | null;
  }>({
    visible: false,
    x: 0,
    y: 0,
    colorToRemove: null,
  });

  // Handler for right click on custom color box
  const onCustomColorRightClick = (event: React.MouseEvent, color: string) => {
    event.preventDefault();
    setContextMenu({
      visible: true,
      x: event.clientX,
      y: event.clientY,
      colorToRemove: color,
    });
  };

  // Handler to remove custom color
  const removeCustomColor = () => {
    if (contextMenu.colorToRemove) {
      setCustomColors(customColors.filter(c => c !== contextMenu.colorToRemove));
    }
    setContextMenu({ visible: false, x: 0, y: 0, colorToRemove: null });
  };

  // Close context menu on outside click
  useEffect(() => {
    const handleClick = () => {
      if (contextMenu.visible) {
        setContextMenu({ visible: false, x: 0, y: 0, colorToRemove: null });
      }
    };
    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, [contextMenu.visible]);

  useEffect(() => {
    if (value) {
      setColor(value);
      if (value.startsWith('#')) {
        setHexColor(value);
      } else {
        setHexColor(extractHex(value));
      }
    }
    if (!disabledAlphaProp && value) {
      // Extract alpha from rgba if available
      const rgbaMatch = value.match(/rgba\(\s*\d+,\s*\d+,\s*\d+,\s*([\d.]+)\s*\)/);
      if (rgbaMatch) {
        const alpha = parseFloat(rgbaMatch[1]);
        const parsedOpacity = Math.round(alpha * 100);
        setOpacity(parsedOpacity);
      } else {
        // fallback if hex or rgb
        setOpacity(100);
      }
    }
  }, [value, disabledAlphaProp]);

  // Convert hex to rgb string (no alpha)
  const hexToRgb = (hex: string): string => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (result) {
      return `rgb(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)})`;
    }
    return hex;
  };

  const handleColorChange = useCallback(
    (value: AggregationColor, _css: string) => {
      const hexValue = value.toHexString();
      if (disabledAlphaProp) {
        const rgbValue = hexToRgb(hexValue);
        setColor(rgbValue);
        setHexColor(hexValue);
        onChange?.(rgbValue);
      } else {
        const rgba = hexToRgba(hexValue, opacity / 100);
        setColor(rgba);
        onChange?.(rgba);
      }
    },
    [onChange, disabledAlphaProp, opacity],
  );

  const handleBoxClick = (c: string) => {
    if (disabledAlphaProp) {
      // No opacity: convert to rgb format
      let rgbColor = c;
      if (c.startsWith('#')) {
        rgbColor = hexToRgb(c);
      } else if (c.startsWith('rgba(')) {
        const match = c.match(/rgba\((\d+),\s*(\d+),\s*(\d+)/);
        if (match) {
          rgbColor = `rgb(${match[1]}, ${match[2]}, ${match[3]})`;
        }
      }
      setColor(rgbColor);
      setHexColor(extractHex(c));
      onChange?.(rgbColor);
    } else {
      // With opacity: convert to rgba format
      if (c.startsWith('rgba(')) {
        const rgba = rgbToRgbaConvert(c, opacity / 100);
        setColor(rgba!);
        onChange?.(rgba!);
        setHexColor(extractHex(rgba!));
      } else {
        const rgba = hexToRgba(c, opacity / 100);
        setColor(rgba);
        onChange?.(rgba);
      }
    }
  };

  const handleOpacityChange = (val: number | null) => {
    const newVal = val ?? 100;
    setOpacity(newVal);

    if (color.startsWith('#')) {
      const rgba = hexToRgba(color, newVal / 100);
      setColor(rgba);
      onChange?.(rgba);
    } else {
      const rgba = rgbToRgbaConvert(color, newVal / 100);
      setColor(rgba!);
      onChange?.(rgba!);
    }
  };

  const fullGrey = [
    '#ffffff',
    '#dbdbdb',
    '#b8b8b8',
    '#9c9c9c',
    '#808080',
    '#636363',
    '#4a4a4a',
    '#2e2e2e',
    '#0f0f0f',
    '#000000',
  ];

  const customPanelRender = (
    _: React.ReactNode,
    { components: { Picker } }: { components: { Picker: React.ComponentType<{ format?: string }> } },
  ) => (
    <Row justify="space-between" wrap={false}>
      <Col>
        {!showPicker && (
          <div className="flex flex-wrap gap-1">
            {/* Row 1: Grey palette left → right */}
            <Row gutter={[4, 0]} wrap={false} className="m-0 p-0">
              {fullGrey.map((c, idx) => (
                <Col key={`grey-${idx}`}>
                  <ColorBox color={c} onClick={() => handleBoxClick(c)} selected={hexColor.startsWith(c)} />
                </Col>
              ))}
            </Row>

            <Row gutter={[4, 0]} wrap={false} className="m-0 p-0">
              {entries(palettes).map(([name, arr]) => (
                <Col key={`${name}-dark`}>
                  <ColorBox
                    color={arr[6]}
                    onClick={() => handleBoxClick(arr[6])}
                    selected={hexColor.startsWith(arr[6])}
                  />
                </Col>
              ))}
            </Row>

            {/* Rows 3–8: Light → dark in columns */}
            <Row gutter={[4, 4]} wrap={false} align="top">
              {entries(palettes).map(([name, arr]) => (
                <Col key={`${name}-col1`}>
                  <div className="flex flex-col gap-1">
                    {arr.slice(1, 5).map((c, idx) => (
                      <ColorBox
                        key={`${name}-${idx}`}
                        color={c}
                        onClick={() => handleBoxClick(c)}
                        selected={hexColor.startsWith(c)}
                      />
                    ))}
                  </div>
                  <div className="flex flex-col gap-1 mt-1">
                    {arr.slice(7, 9).map((c, idx) => (
                      <ColorBox
                        key={`${name}-${idx}`}
                        color={c}
                        onClick={() => handleBoxClick(c)}
                        selected={hexColor.startsWith(c)}
                      />
                    ))}
                  </div>
                </Col>
              ))}
            </Row>

            <Divider className="my-0 p-0" />

            {/* Custom colors section */}
            <Row align="middle" gutter={[8, 4]} className="mt-1 mr-0 ml-0">
              {customColors.map((c, idx) => (
                <Col key={`${c}-col2`}>
                  <Row gutter={[4, 4]}>
                    <Col key={`custom-${idx}`} className="mt-0 mr-0 ml-0 mb-0">
                      <ColorBox
                        color={c}
                        onClick={() => handleBoxClick(c)}
                        selected={extractHex(color) === extractHex(c)}
                        onContextMenu={e => onCustomColorRightClick(e, c)} // <-- add right click handler here
                      />
                    </Col>
                  </Row>
                </Col>
              ))}

              <Col>
                <PlusOutlined
                  className="text-[15px] cursor-pointer select-non ml-0"
                  onClick={() => setShowPicker(true)}
                />
              </Col>

              {/* Context menu */}
              {contextMenu.visible && (
                <div
                  className={`${styles.delete_toggle}`}
                  style={{ top: contextMenu.y + 3, left: contextMenu.x }}
                  onClick={removeCustomColor}
                >
                  <DeleteOutlined />
                  <span className="text-xs ml-1">Remove color</span>
                </div>
              )}
            </Row>

            {!disabledAlphaProp && (
              <Row>
                <Col flex="none" className="w-full font-[12px]">
                  Opacity
                </Col>
                <Col flex="auto">
                  <OpacitySlider
                    color={color}
                    min={0}
                    max={100}
                    value={opacity}
                    onChange={handleOpacityChange}
                    disabled={disabled}
                    className={`${styles.sliderColorPicker}`}
                  />
                </Col>
                <Col flex="none">
                  <InputNumber
                    min={0}
                    max={100}
                    value={opacity}
                    formatter={val => `${val}%`}
                    parser={val => parseInt(val?.replace('%', '') || '0', 10)}
                    style={{ width: 55, fontSize: 12 }}
                    onChange={handleOpacityChange}
                  />
                </Col>
              </Row>
            )}
          </div>
        )}
      </Col>
      <Col flex="auto">
        {showPicker && (
          <div className="flex flex-col gap-1">
            {/* Color picker square + vertical slider */}
            <Picker format="hex" />

            {/* Input + Add button */}
            <div className={'flex justify-center'}>
              {/* <Input
                value={value}
                onChange={(e) => {
                  const newHex = e.target.value;
                  setDisplayHex(newHex); // update what user sees
                  const rgba = rgbToRgbaConvert(newHex, opacity / 100);
                  setColor(rgba!);
                  onChange?.(rgba!);
                }}
                prefix=""
                style={{
                  flex: 1,
                  fontSize: 10,
                  padding: '0px 0px 0px 4px',
                  fontWeight: 'bold',
                }}
              /> */}

              <Button
                type="primary"
                size="small"
                onClick={() => {
                  if (!customColors.includes(color)) {
                    setCustomColors([...customColors, color]);
                  }
                  setShowPicker(false);
                }}
              >
                Add
              </Button>
            </div>
          </div>
        )}
      </Col>
    </Row>
  );

  const colorPickerElement = (
    <AntdColorPicker
      value={color}
      onChange={handleColorChange}
      disabled={disabled}
      panelRender={customPanelRender}
      styles={{ popupOverlayInner: { width: 220 } }}
      disabledAlpha={!!disabledAlphaProp}
      disabledFormat
      onOpenChange={open => {
        if (!open) {
          setShowPicker(false);
        }
      }}
    >
      {isFromMiniToolbar ? (
        <Tooltip title={label || 'Line tools colors'}>
          <div
            className="flex flex-col items-center cursor-pointer border rounded px-1 pt-[4px] pb-[2px]"
            style={{
              color: 'var(--foreColor)',
              borderColor: isHovered ? 'var(--accentColor, #1890ff)' : 'var(--borderColor, #d1d5db)',
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {SvgIcon && <SvgIcon className="text-[12px]" />}
            <div
              className={`mt-[2px]`}
              style={{
                backgroundColor: color,
                width: '22px',
                height: '5px',
                border: color ? `1px ${color}` : 'none',
                borderRadius: '8px',
              }}
            />
          </div>
        </Tooltip>
      ) : (
        <div className="flex items-center gap-2 cursor-pointer">
          <div
            style={{
              backgroundColor: color,
              width: '24px',
              height: '24px',
              borderRadius: '4px',
              border: '1px solid #d1d5db',
            }}
          />
        </div>
      )}
    </AntdColorPicker>
  );

  // Render with label for non-mini-toolbar mode
  if (!isFromMiniToolbar && label) {
    return (
      <div className={styles.property_row}>
        <label className={styles.property_label}>{label}</label>
        <div className={styles.property_value}>{colorPickerElement}</div>
      </div>
    );
  }

  return colorPickerElement;
};

export default ColorPicker;
